import { Component, Inject, OnInit } from '@angular/core';
import { AbstractParametrageService, PARAMETRAGE_SERVICE_TOKEN } from './abstract-parametrage.service';
import { finalize } from 'rxjs/operators';
import { LigneParametrage } from './ligne-parametrage.interface';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { CommonModule } from '@angular/common';
import { ColonneParametrage } from './colonne-parametrage.model';
import { InputTextModule } from 'primeng/inputtext';
import { FooterBarComponent } from '../angular/footerBar/footerBar.component';
import { AbstractSigaleComponent } from '../abstract-sigale.component';
import { TranslateModule } from '@ngx-translate/core';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { AlertService } from '../alert/alert.service';
import { AlertComponent } from '../alert/alert.component';

@Component({
	selector: 'parametrage',
	templateUrl: './parametrage.component.html',
	styleUrls: ['./parametrage.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		ReactiveFormsModule,
		InputTextModule,
		TableModule,
		FooterBarComponent,
		TranslateModule,
		ToastModule,
		AlertComponent
	]
})
export class ParametrageComponent extends AbstractSigaleComponent implements OnInit {

	readonly toastKey: string = 'parametrageComponentToastKey';

	loading: boolean;

	colonnes: ColonneParametrage[] = [];
	ligneParametrages: LigneParametrage[] = [];

	formParId: Map<number, FormGroup> = new Map();
	form: FormArray<FormGroup>;
	formModifie = false;

	constructor(
		@Inject(PARAMETRAGE_SERVICE_TOKEN) private parametrageService: AbstractParametrageService,
		private alertService: AlertService,
		private messageService: MessageService,
		private formBuilder: FormBuilder) {
		super();
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.nomAlert = 'parametrageComponentAlert';
		this.recupererParametrage();
	}

	recupererParametrage(): void {
		this.loading = true;
		this.alertService.clear(this.nomAlert);
		this.parametrageService.recupererParametrage()
			.pipe(finalize(() => this.loading = false))
			.subscribe(parametrage => {
					this.colonnes = parametrage.colonnes;
					this.ligneParametrages = parametrage.ligneParametrages;
					this.form = this.formBuilder.array<FormGroup>([]);

					parametrage.ligneParametrages.forEach(ligneParametrage => {
						const formgroup = this.formBuilder.group({});
						this.colonnes.forEach(
							colonne => formgroup.addControl(
								colonne.key,
								this.formBuilder.control(colonne.getValeur(ligneParametrage))
							));
						this.formParId.set(ligneParametrage.id, formgroup);
						this.form.push(formgroup);
					});

					this.form.valueChanges.subscribe(() => this.formModifie = true);
				},
				error => this.alertService.error(error.error, this.nomAlert)
			);
	}

	enregistrer(): void {
		const lignes = [...this.formParId.entries()]
			.filter(([, form]) => form.touched && !form.pristine)
			.map(([id, form]) => {
				const ligneParametrage = new LigneParametrage().deserialize(form.getRawValue());
				ligneParametrage.id = id; // utile pour les applications où le code n'est pas unique dans la table paramétrage.
				return ligneParametrage;
			});

		this.loading = true;
		this.alertService.clear(this.nomAlert);
		this.parametrageService.mettreAJour(lignes)
			.pipe(finalize(() => this.loading = false))
			.subscribe(() => {
				this.formModifie = false;
				this.afficherToastSuccess(this.translateService.instant('SIGALE_ANGULAR.COMPONENTS.PARAMETRAGE.OPERATION_REUSSIE'));
			}, error => this.alertService.error(error.error, this.nomAlert));
	}

	appliquerFiltre(filter: Function, event: Event): void {
		filter((event.target as HTMLInputElement).value);
	}

	get enregistrementInactif(): boolean {
		return this.form == null || !this.formModifie;
	}

	get tailleColonneStandard(): string {
		return this.colonnes == null
			? 'auto'
			: `${(1 / this.colonnes?.length) * 100}%`;
	}

	afficherToastSuccess(message: string): void {
		this.messageService.add({ key: this.toastKey, severity: 'success', detail: message });
	}

	traductionFR(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'PARAMETRAGE': {
						'AUCUN_PARAMETRAGE': 'Aucun paramétrage existant.',
						'ENREGISTRER': 'Enregistrer',
						'OPERATION_REUSSIE': 'Opération réussie'
					}
				}
			}
		};
	}

	traductionIT(): any {
		return {
			'SIGALE_ANGULAR': {
				'COMPONENTS': {
					'PARAMETRAGE': {
						'AUCUN_PARAMETRAGE': 'Nessuna impostazione existente.',
						'ENREGISTRER': 'Registrare',
						'OPERATION_REUSSIE': 'Operazione riuscita'
					}
				}
			}
		};
	}

}
